import { useState } from "react";
import type { SeqGap } from "@sportsos/engine";
import type { SyncState } from "@sportsos/sync";
import type { StuckEvent } from "../match/useLedger.ts";

interface Words {
  readonly label: string;
  readonly tone: "good" | "working" | "warn" | "bad";
}

function words(state: SyncState): Words {
  switch (state.kind) {
    case "idle":
      return { label: "Ready", tone: "good" };
    case "synced":
      return { label: "Synced", tone: "good" };
    case "syncing":
      return { label: `Syncing (${state.pending})`, tone: "working" };
    case "saved":
      return { label: `Saved (${state.pending})`, tone: "warn" };
    case "offline":
      return { label: `Offline · ${state.pending}`, tone: "warn" };
    case "blocked":
      return { label: `${state.blocked} pending`, tone: "bad" };
    case "signin":
      return { label: `Sign in to sync (${state.pending})`, tone: "bad" };
    case "foreign":
      return { label: `Other account · ${state.foreign}`, tone: "bad" };
  }
}

const badgeVariant: Record<Words["tone"], string> = {
  good: "ds-badge--success",
  working: "ds-badge--accent",
  warn: "ds-badge--warning",
  bad: "ds-badge--danger",
};

export function SyncPill({
  state,
  onSignIn,
}: {
  readonly state: SyncState;
  /** Offered only when the session has lapsed, and only if the caller can do something. */
  readonly onSignIn?: () => void;
}): React.ReactElement {
  const { label, tone } = words(state);
  const pill = (
    <span className={`ds-badge ${badgeVariant[tone]}`} role="status" aria-live="polite">
      {label}
    </span>
  );
  if (state.kind !== "signin" || onSignIn === undefined) return pill;
  // The balls are safe on the device; this is the way to the screen that lets them move. Not an
  // automatic redirect, because being thrown to a sign-in form mid-over is worse than a pill.
  return (
    <button
      type="button"
      onClick={onSignIn}
      style={{ background: "none", border: 0, padding: 0, cursor: "pointer" }}
    >
      {pill}
    </button>
  );
}

/**
 * Shown when the balls on this device were written by a different account from the one signed
 * in. They are not sent, and they are not deleted until the scorer says so.
 */
export function ForeignBanner({
  state,
  onDiscard,
}: {
  readonly state: SyncState;
  readonly onDiscard: () => void;
}): React.ReactElement | null {
  if (state.kind !== "foreign") return null;
  return (
    <div
      className="ds-card ds-card--flat"
      role="alert"
      style={{
        padding: "var(--ds-space-2) var(--ds-space-3)",
        marginBottom: "var(--ds-space-2)",
        fontSize: "var(--ds-text-xs)",
        color: "var(--ds-danger-600)",
        background: "var(--ds-danger-100)",
        borderColor: "transparent",
      }}
    >
      <div>
        {state.foreign} {state.foreign === 1 ? "ball was" : "balls were"} scored by another
        account, so {state.foreign === 1 ? "it is" : "they are"} not being sent. Sign back in as
        that account to send {state.foreign === 1 ? "it" : "them"}.
      </div>
      <button type="button" className="ds-btn ds-btn--sm" onClick={onDiscard}>
        Discard {state.foreign === 1 ? "it" : "them"}
      </button>
    </div>
  );
}

export function GapBanner({ gaps }: { readonly gaps: readonly SeqGap[] }): React.ReactElement | null {
  const [dismissed, setDismissed] = useState(false);
  if (gaps.length === 0 || dismissed) return null;
  return (
    <div
      className="ds-card ds-card--flat"
      role="alert"
      style={{
        padding: "var(--ds-space-2) var(--ds-space-3)",
        marginBottom: "var(--ds-space-2)",
        fontSize: "var(--ds-text-xs)",
        color: "var(--ds-warning-600)",
        background: "var(--ds-warning-100)",
        borderColor: "transparent",
        display: "flex",
        alignItems: "center",
        gap: "var(--ds-space-2)",
      }}
    >
      <span style={{ flex: 1 }}>
        Sync gap detected · match data may be incomplete
      </span>
      <button
        className="ds-btn ds-btn--ghost ds-btn--sm"
        style={{ padding: "0 var(--ds-space-2)", minHeight: 28 }}
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
      >
        &times;
      </button>
    </div>
  );
}

export function StuckBanner({
  stuck,
}: {
  readonly stuck: readonly StuckEvent[];
}): React.ReactElement | null {
  const [dismissed, setDismissed] = useState(false);
  if (stuck.length === 0 || dismissed) return null;
  return (
    <div
      className="ds-card ds-card--flat"
      role="alert"
      style={{
        padding: "var(--ds-space-2) var(--ds-space-3)",
        marginBottom: "var(--ds-space-2)",
        fontSize: "var(--ds-text-xs)",
        color: "var(--ds-warning-600)",
        background: "var(--ds-warning-100)",
        borderColor: "transparent",
        display: "flex",
        alignItems: "center",
        gap: "var(--ds-space-2)",
      }}
    >
      <span style={{ flex: 1 }}>
        {stuck.length} {stuck.length === 1 ? "ball" : "balls"} saved locally · will retry
      </span>
      <button
        className="ds-btn ds-btn--ghost ds-btn--sm"
        style={{ padding: "0 var(--ds-space-2)", minHeight: 28 }}
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
      >
        &times;
      </button>
    </div>
  );
}
